import type {
  AdminInspector,
  AdminOrg,
  AdminProfessional,
  AdminSource,
  RuleRelease,
  SupportGrant,
  WebhookDelivery,
} from './types.js';

/** Seed data for an offline-interactive platform-ops console. `NOW` anchors time-boxed views. */
export const NOW = '2026-06-15T12:00:00.000Z';

export const SOURCES: AdminSource[] = [
  {
    id: 'src_eudamed',
    name: 'EUDAMED (dataset)',
    capabilities: ['resolve', 'verify', 'certificate'],
    protocol: 'dataset',
    auth: 'none',
    residency: 'eu',
    status: 'live',
    connection: 'connected',
    mapping_version: 'eudamed@4.2.1',
    freshness_window_seconds: 86_400,
    health: { region: 'eu', reachable: true, last_success_age_seconds: 5_400, quota_used_today: 0, quota_limit_per_day: null, circuit: 'closed' },
  },
  {
    id: 'src_companies_house',
    name: 'Companies House',
    capabilities: ['resolve', 'verify', 'list'],
    protocol: 'rest',
    auth: 'api_key',
    residency: 'uk',
    status: 'live',
    connection: 'connected',
    mapping_version: 'ch@2.0.0',
    freshness_window_seconds: 3_600,
    health: { region: 'uk', reachable: true, last_success_age_seconds: 240, quota_used_today: 4_200, quota_limit_per_day: 6_000, circuit: 'closed' },
  },
  {
    id: 'src_nando',
    name: 'NANDO notified bodies',
    capabilities: ['certificate', 'list'],
    protocol: 'file_feed',
    auth: 'none',
    residency: 'eu',
    status: 'degraded',
    connection: 'connected',
    mapping_version: 'nando@1.3.0',
    freshness_window_seconds: 43_200,
    health: { region: 'eu', reachable: true, last_success_age_seconds: 61_000, quota_used_today: 12, quota_limit_per_day: 500, circuit: 'half_open' },
  },
  {
    id: 'src_mca21',
    name: 'MCA21 (RPA)',
    capabilities: ['resolve', 'verify'],
    protocol: 'portal_rpa',
    auth: 'actor_credential',
    residency: 'in',
    status: 'down',
    connection: 'error',
    mapping_version: 'mca21@0.9.0',
    freshness_window_seconds: 21_600,
    health: { region: 'in', reachable: false, last_success_age_seconds: 172_800, quota_used_today: 0, quota_limit_per_day: 1_200, circuit: 'open' },
  },
  {
    id: 'src_ded',
    name: 'Dubai DED',
    capabilities: ['resolve', 'verify'],
    protocol: 'rest',
    auth: 'oauth',
    residency: 'ae',
    status: 'planned',
    connection: 'connected',
    mapping_version: 'ded@0.4.0',
    freshness_window_seconds: 7_200,
    health: { region: 'ae', reachable: true, last_success_age_seconds: 900, quota_used_today: 0, quota_limit_per_day: 2_000, circuit: 'closed' },
  },
  {
    id: 'src_freezone',
    name: 'JAFZA free-zone registry',
    capabilities: ['resolve'],
    protocol: 'portal_rpa',
    auth: 'actor_credential',
    residency: 'ae',
    status: 'planned',
    connection: 'connecting',
    mapping_version: 'jafza@0.2.0',
    freshness_window_seconds: 7_200,
    health: { region: 'ae', reachable: false, last_success_age_seconds: 0, quota_used_today: 0, quota_limit_per_day: null, circuit: 'closed' },
  },
];

export const RULE_RELEASES: RuleRelease[] = [
  {
    id: 'rel_2026_06_3',
    name: '2026.06.3',
    cut_at: '2026-06-12T09:00:00.000Z',
    cut_by: 'rules-bot',
    state: 'live',
    signed: true,
    changelog: 'EU MDR Annex VIII clarifications; UK MDR 2002 crosswalk refresh; 2 retired rules.',
    rules: [
      { rule_identifier: 'EU-MDR-AnnexVIII-R12', title: 'Active device classification — rule 12', domain: 'medical', jurisdiction: 'eu', version: 7, status: 'published' },
      { rule_identifier: 'EU-MDR-AnnexVIII-R11', title: 'Software as a medical device — rule 11', domain: 'medical', jurisdiction: 'eu', version: 4, status: 'published' },
      { rule_identifier: 'UK-MDR2002-Part-II', title: 'UKCA classification crosswalk', domain: 'medical', jurisdiction: 'uk', version: 3, status: 'published' },
      { rule_identifier: 'EU-ECE-R10-EMC', title: 'Automotive EMC type approval', domain: 'automotive', jurisdiction: 'eu', version: 2, status: 'published' },
    ],
  },
  {
    id: 'rel_2026_05_8',
    name: '2026.05.8',
    cut_at: '2026-05-28T09:00:00.000Z',
    cut_by: 'a.okafor',
    state: 'superseded',
    signed: true,
    changelog: 'Sanctions list crosswalk update; insurance certificate scope rule added.',
    rules: [
      { rule_identifier: 'EU-MDR-AnnexVIII-R12', title: 'Active device classification — rule 12', domain: 'medical', jurisdiction: 'eu', version: 6, status: 'superseded' },
      { rule_identifier: 'EU-DualUse-2021-821', title: 'Export-control dual-use screen', domain: 'sanctions_export', jurisdiction: 'eu', version: 1, status: 'published' },
    ],
  },
  {
    id: 'rel_2026_05_2',
    name: '2026.05.2',
    cut_at: '2026-05-09T09:00:00.000Z',
    cut_by: 'a.okafor',
    state: 'rolled_back',
    signed: false,
    changelog: 'Reverted: GMDN nomenclature import regressed 14 classifications.',
    rules: [
      { rule_identifier: 'EU-MDR-AnnexVIII-R12', title: 'Active device classification — rule 12', domain: 'medical', jurisdiction: 'eu', version: 5, status: 'retired' },
    ],
  },
];

export const INSPECTORS: AdminInspector[] = [
  {
    id: 'insp_8841',
    name: 'M. Haddad',
    kind: 'field_inspector',
    standing: 'active',
    coverage: { jurisdictions: ['ae', 'eu', 'uk'], asset_classes: ['medical', 'electronics', 'semiconductor'], methods: ['physical_inspection', 'manufacturer_callback', 'document_forensics'] },
    credentials: [
      { name: 'IEC 62353 field-safety', issuer: 'TÜV SÜD', expires: '2027-03-01', state: 'valid' },
      { name: 'Medical-device handling', issuer: 'Garnet Academy', expires: '2026-07-02', state: 'expiring' },
    ],
    rating: { sla_hit_rate: 0.97, dispute_rate: 0.02 },
    open_disputes: 0,
  },
  {
    id: 'lab_2207',
    name: 'Northbridge Test Labs',
    kind: 'test_lab',
    standing: 'active',
    coverage: { jurisdictions: ['uk', 'eu'], asset_classes: ['electronics', 'semiconductor'], methods: ['as6171_test', 'document_forensics'] },
    credentials: [{ name: 'AS6171 full scope', issuer: 'UKAS', expires: '2026-12-31', state: 'valid' }],
    rating: { sla_hit_rate: 0.91, dispute_rate: 0.05 },
    open_disputes: 1,
  },
  {
    id: 'insp_8830',
    name: 'R. Okonkwo',
    kind: 'field_inspector',
    standing: 'probation',
    coverage: { jurisdictions: ['ae'], asset_classes: ['medical'], methods: ['physical_inspection'] },
    credentials: [{ name: 'Medical-device handling', issuer: 'Garnet Academy', expires: '2026-05-20', state: 'expired' }],
    rating: { sla_hit_rate: 0.78, dispute_rate: 0.14 },
    open_disputes: 2,
  },
];

export const PROFESSIONALS: AdminProfessional[] = [
  { id: 'pro_n14', name: 'Al Tamimi & Co', type: 'notary', jurisdiction: 'ae', country: 'United Arab Emirates', status: 'active', languages: ['ar', 'en'] },
  { id: 'pro_t08', name: 'Lingua Verita', type: 'translator', jurisdiction: 'eu', country: 'Germany', status: 'active', languages: ['de', 'en', 'fr'] },
  { id: 'pro_l03', name: 'Moreland Trust Law', type: 'trust_lawyer', jurisdiction: 'uk', country: 'United Kingdom', status: 'probation', languages: ['en'] },
];

export const ORGS: AdminOrg[] = [
  { id: 'org_lench', legal_name: 'Lench Procurement Ltd', home_region: 'uk', plan: 'scale', status: 'active', residency: { region_pinned: true, forbid_third_party_processing: true }, created_at: '2025-02-11T00:00:00.000Z', seats: 42, records_30d: 1_284 },
  { id: 'org_meridian', legal_name: 'Meridian Health Systems', home_region: 'eu', plan: 'enterprise', status: 'active', residency: { region_pinned: true, forbid_third_party_processing: true }, created_at: '2024-09-03T00:00:00.000Z', seats: 130, records_30d: 5_902 },
  { id: 'org_crescent', legal_name: 'Crescent Components DMCC', home_region: 'ae', plan: 'growth', status: 'active', residency: { region_pinned: true, forbid_third_party_processing: false }, created_at: '2025-11-20T00:00:00.000Z', seats: 12, records_30d: 318 },
  { id: 'org_apex', legal_name: 'Apex Devices (suspended)', home_region: 'eu', plan: 'starter', status: 'suspended', residency: { region_pinned: true, forbid_third_party_processing: true }, created_at: '2025-06-01T00:00:00.000Z', seats: 4, records_30d: 0 },
];

export const SUPPORT_GRANTS: SupportGrant[] = [
  { id: 'grant_5512', org_id: 'org_meridian', org_name: 'Meridian Health Systems', granted_to: 's.varga (support)', reason: 'CASE-7740 record composition stuck in composing', scope: 'record:rec_7K2P read-only', granted_at: '2026-06-15T11:30:00.000Z', expires_at: '2026-06-15T13:30:00.000Z', status: 'active', audit_ref: 'audit_evt_991204' },
  { id: 'grant_5498', org_id: 'org_lench', org_name: 'Lench Procurement Ltd', granted_to: 'j.mbeki (support)', reason: 'Webhook dead-letter investigation', scope: 'webhooks read-only', granted_at: '2026-06-14T08:00:00.000Z', expires_at: '2026-06-14T10:00:00.000Z', status: 'expired', audit_ref: 'audit_evt_990877' },
  { id: 'grant_5470', org_id: 'org_crescent', org_name: 'Crescent Components DMCC', granted_to: 's.varga (support)', reason: 'Onboarding import review (revoked early)', scope: 'counterparties read-only', granted_at: '2026-06-10T14:00:00.000Z', expires_at: '2026-06-10T18:00:00.000Z', status: 'revoked', audit_ref: 'audit_evt_989122' },
];

export const WEBHOOKS: WebhookDelivery[] = [
  { id: 'whd_88210', org_name: 'Meridian Health Systems', event: 'recall.matched', endpoint: 'https://hooks.meridian.example/garnet', status: 'delivered', attempts: 1, max_attempts: 6, last_attempt_at: '2026-06-15T11:58:00.000Z', response_code: 200 },
  { id: 'whd_88204', org_name: 'Lench Procurement Ltd', event: 'record.issued', endpoint: 'https://api.lench.example/inbound/garnet', status: 'failed', attempts: 3, max_attempts: 6, last_attempt_at: '2026-06-15T11:40:00.000Z', response_code: 503 },
  { id: 'whd_88180', org_name: 'Lench Procurement Ltd', event: 'flag.raised', endpoint: 'https://api.lench.example/inbound/garnet', status: 'dead_letter', attempts: 6, max_attempts: 6, last_attempt_at: '2026-06-15T09:12:00.000Z', response_code: 500 },
  { id: 'whd_88176', org_name: 'Crescent Components DMCC', event: 'case.assigned', endpoint: 'https://crescent.example/webhooks', status: 'pending', attempts: 0, max_attempts: 6, last_attempt_at: '2026-06-15T11:59:30.000Z', response_code: null },
  { id: 'whd_88150', org_name: 'Meridian Health Systems', event: 'attestation.completed', endpoint: 'https://hooks.meridian.example/garnet', status: 'delivered', attempts: 2, max_attempts: 6, last_attempt_at: '2026-06-15T10:05:00.000Z', response_code: 200 },
];
